/** @forge-header
 * @id          spreadParsers
 * @role        Spread ticker parsers | all sports spread market parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,SpreadInfo,TickerCategory | kalshiTicker/helpers:labelToSport,parseFallback | nbaConsolidated/teamNames:teamNameFromCode
 * @downstream  kalshiTicker/parse
 * @exports     parseNbaSpread:fn | parseNflSpread:fn | parseNhlSpread:fn | parseNcaaMbSpread:fn | parseNcaaWbSpread:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo, SpreadInfo, TickerCategory } from '../types';
import { parseDateFromTicker } from '../types';
import { labelToSport, parseFallback, mlbTeamFromCode } from '../helpers';
import { teamNameFromCode, splitWnbaTeams } from '../teamNames';

/** Splitter signature: take a packed team-pair chunk, return [away, home] or null. */
type TeamSplitter = (pair: string) => [string, string] | null;

/** Default splitter: fixed 3+3 slice (NBA, NFL, NHL, MLB, NCAA). */
function defaultSplit(pair: string): [string, string] | null {
  if (pair.length < 6) return null;
  return [pair.slice(0, 3), pair.slice(3, 6)];
}

/** NBA spread: KXNBASPREAD-26FEB04BOSMIA-BOS */
export function parseNbaSpread(raw: string): ParsedTicker {
  return parseSpreadGeneric(raw, 'KXNBASPREAD', 'nba-spread', 'NBA');
}

/** WNBA spread: KXWNBASPREAD-26MAY13INDLA-LA (variable-length codes) */
export function parseWnbaSpread(raw: string): ParsedTicker {
  return parseSpreadGeneric(raw, 'KXWNBASPREAD', 'wnba-spread', 'WNBA', splitWnbaTeams);
}

/** NFL spread: KXNFLSPREAD-26FEB08SEANE-SEA */
export function parseNflSpread(raw: string): ParsedTicker {
  return parseSpreadGeneric(raw, 'KXNFLSPREAD', 'nfl-spread', 'NFL');
}

/** NHL spread: KXNHLSPREAD-26FEB08SEATOR-SEA */
export function parseNhlSpread(raw: string): ParsedTicker {
  return parseSpreadGeneric(raw, 'KXNHLSPREAD', 'nhl-spread', 'NHL');
}

/** NCAA Men's Basketball spread */
export function parseNcaaMbSpread(raw: string): ParsedTicker {
  return parseSpreadGeneric(raw, 'KXNCAAMBSPREAD', 'ncaa-spread', 'NCAAMB');
}

/**
 * Soccer full-game goal spread: KXWCSPREAD-26JUN14CIVECU-CIV2 (also UECL /
 * INTLFRIENDLY competitions). The 3-way moneyline is grouped via marketStore's
 * multi-outcome path; each spread line is an ordinary 2-way binary, so it
 * parses exactly like a binary sport's spread (3-char FIFA country codes →
 * default 3+3 split). The series stem varies by competition, so read it off
 * the raw rather than hardcoding one prefix.
 */
export function parseSoccerSpread(raw: string): ParsedTicker {
  const series = raw.split('-')[0] || 'KXWCSPREAD';
  return parseSpreadGeneric(raw, series, 'soccer-spread', 'SOCCER');
}

/** NCAA Women's Basketball spread */
export function parseNcaaWbSpread(raw: string): ParsedTicker {
  return parseSpreadGeneric(raw, 'KXNCAAWBSPREAD', 'ncaa-spread', 'NCAAWB');
}

/**
 * MLB spread: KXMLBSPREAD-26MAY12NYYBAL-NYY7 or
 * KXMLBSPREAD-26MAY121610NYYBAL-NYY7 (with optional HHMM start time).
 *
 * MLB has 2-char codes (KC, SD, SF, TB) and 3-char codes. The outcome is
 * `{TEAM}{BUCKET}` — the team-code prefix is the favored side, which we use
 * to disambiguate the matchup the same way parseMlbGame uses the YES team.
 */
export function parseMlbSpread(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 3) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 7) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  if (!date) return parseFallback(raw);

  // Strip optional HHMM block after the date.
  let teamBlob = mid.slice(7);
  if (/^\d{4}/.test(teamBlob)) teamBlob = teamBlob.slice(4);

  const outcome = parts[2] ?? '';
  // outcome = "{TEAM}{BUCKET}" — pull the leading letters as the favored team.
  const outcomeMatch = outcome.match(/^([A-Z]+)(\d+)$/);
  if (!outcomeMatch) return parseFallback(raw);
  const favoredTeam = outcomeMatch[1];

  let awayCode: string;
  let homeCode: string;
  if (teamBlob.endsWith(favoredTeam) && teamBlob.length > favoredTeam.length) {
    awayCode = teamBlob.slice(0, -favoredTeam.length);
    homeCode = favoredTeam;
  } else if (teamBlob.startsWith(favoredTeam) && teamBlob.length > favoredTeam.length) {
    awayCode = favoredTeam;
    homeCode = teamBlob.slice(favoredTeam.length);
  } else {
    return parseFallback(raw);
  }

  if (!awayCode || !homeCode) return parseFallback(raw);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: mlbTeamFromCode(awayCode) ?? undefined,
    homeName: mlbTeamFromCode(homeCode) ?? undefined,
  };

  const awayDisplay = game.awayName ?? awayCode;
  const homeDisplay = game.homeName ?? homeCode;
  const shortLabel = `MLB Spread: ${awayCode} @ ${homeCode}`;
  const fullLabel = `MLB Spread: ${awayDisplay} @ ${homeDisplay} · ${outcome}`;

  const spread: SpreadInfo = {
    favoredTeam,
    spreadValue: 0,
    side: 'cover',
  };

  return {
    raw,
    category: 'mlb-spread',
    series: 'KXMLBSPREAD',
    date,
    game,
    player: null,
    spread,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/** Generic spread parser for all sports */
function parseSpreadGeneric(
  raw: string,
  series: string,
  category: TickerCategory,
  label: string,
  splitter: TeamSplitter = defaultSplit
): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  // WNBA codes can be as short as 2 chars → mid as short as 7+2+2 = 11.
  if (mid.length < 11) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);

  const split = splitter(teamCodes);
  if (!split) {
    return parseFallback(raw);
  }

  const [awayCode, homeCode] = split;
  const teamCode = parts[2] ?? null;

  const game: GameInfo = { awayCode, homeCode };

  // Get team names for sports with lookup tables
  const sport = labelToSport(label);
  if (sport) {
    game.awayName = teamNameFromCode(awayCode, sport);
    game.homeName = teamNameFromCode(homeCode, sport);
  }

  const awayDisplay = game.awayName ?? awayCode;
  const homeDisplay = game.homeName ?? homeCode;

  const shortLabel = `${label} Spread: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `${label} Spread: ${awayDisplay} @ ${homeDisplay} · ${teamCode}`
    : `${label} Spread: ${awayDisplay} @ ${homeDisplay}`;

  // Note: Actual spread values come from market data, not ticker
  const spread: SpreadInfo = {
    favoredTeam: teamCode ?? awayCode,
    spreadValue: 0, // Will be populated from market data
    side: 'cover',
  };

  return {
    raw,
    category,
    series,
    date,
    game,
    player: null,
    spread,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}
